export type AgentFeatures = Record<string, boolean>;

export const CREDENTIAL_TRANSFER_ALGORITHM = "RSA-OAEP-256+A256GCM" as const;

export interface AgentCredentialPublicKey {
  type: "agent:credential_key";
  v: 1;
  alg: typeof CREDENTIAL_TRANSFER_ALGORITHM;
  kid: string;
  spki: string;
  cert?: string | null;
  issuedAt?: string | null;
  expiresAt?: string | null;
  encoding?: "base64";
}

export interface EncryptedCredential {
  v: 1;
  alg: typeof CREDENTIAL_TRANSFER_ALGORITHM;
  kid: string;
  encryptedKey: string;
  iv: string;
  ciphertext: string;
  encoding: "base64";
}

export interface AgentDependencyItem {
  id: string;
  label: string;
  state: string;
  required: boolean;
  requiresAdmin: boolean;
  systemInstallable: boolean;
  reason: string;
  codec?: string | null;
  downloadUrl?: string | null;
  installCommand?: string | null;
  localPath?: string | null;
  error?: string | null;
}

export interface AgentDependencyReport {
  downloadDirectory: string;
  accountName: string;
  isAdmin: boolean;
  isLocalSystem: boolean;
  displayAdapters: { name: string; vendor: string; driverVersion?: string | null }[];
  items: AgentDependencyItem[];
}

export interface PeerInfo {
  id: string;
  role: "operator" | "operator-media" | "agent" | "client";
  connectedAt: number;
  label: string;
  platform: string;
  active: boolean;
  muxPeerIdx?: number;
  muxReady?: boolean;
  muxOperatorIdx?: number;
}

export type AccessLevel = "view" | "control" | "privacyScreen" | "localInputBlock";
export type AccessState = "pending" | "approved" | "denied" | "not_required" | "off" | "on";

export interface AccessSnapshot {
  view: AccessState;
  control: AccessState;
  privacyScreen: AccessState;
  localInputBlock: AccessState;
}

/**
 * Sous-ensemble du contrat HostPolicy C# (cf. RemoteWS.Host.Service/HostPolicy.cs).
 * Tous les champs sont optionnels côté UI car les sessions héritées (avant
 * Lot 1) n'en publient aucun. La résolution effective (GPO > registre > config)
 * est faite côté host ; le UI ne fait que afficher le résultat.
 *
 * Les enums sont alignés sur le contrat C# (cf. host-policy-schema.ts pour
 * la source unique canonique des valeurs autorisées).
 */
export interface HostPolicy {
  // Connectivité
  relayUrl?: string | null;
  allowRelayOverride?: boolean;
  allowedRelayHosts?: string[] | null;
  blockedDomains?: string[] | null;
  // Affichage virtuel (option lab)
  allowVirtualOperatorDisplay?: boolean;
  virtualDisplayProvider?: "none" | "virtualdrivers-vdd" | "vdd" | "remotews-idd" | string;
  virtualDisplayRequiresPrivacyScreen?: boolean;
  allowWindowMoveToVirtualDisplay?: boolean;
  allowMoveExistingWindowsToVirtualDisplay?: boolean;
  restoreMovedWindowsOnStop?: boolean;
  // Consentement
  viewConsentMode?: "never" | "always" | "firstOperator";
  controlConsentMode?: "never" | "always" | "onFirstInput";
  consentTimeoutSeconds?: number;
  consentTimeoutAction?: "approve" | "deny";
  showOperatorIdentity?: boolean;
  allowViewOnlyWithoutControl?: boolean;
  // Privacy / input
  allowPrivacyScreen?: boolean;
  privacyScreenRequiresConsent?: boolean;
  allowLocalInputBlock?: boolean;
  localInputBlockRequiresPrivacyScreen?: boolean;
  // Power / shutdown
  allowPowerHoldDuringRemoteWork?: boolean;
  powerHoldMaxMinutes?: number;
  allowShutdownBlockDuringRemoteWork?: boolean;
  shutdownBlockMaxMinutes?: number;
  shutdownBlockReason?: string;
  // RDS
  rdsTargetMode?: "explicit" | "consoleOnly" | "activeSingle";
  rdpAdaptiveResolutionAllowed?: boolean;
  // Lot 6 RDP Shadow
  rdpShadowMode?: "disabled" | "helper_inject" | "credential_prompt";
  rdpShadowDefaultAction?: "view" | "control";
  rdpShadowSuppressNativePrompt?: boolean;
  rdpShadowMaxMinutes?: number;
  rdpShadowAllowedGroups?: string;
  // Lot E RDP Shadow PC classique
  rdpShadowConsent?: "direct" | "credential_prompt";
  // Lot 7 Unattended Access
  unattendedAccessAllowed?: boolean;
  unattendedAccessAllowedGroups?: string;
  unattendedAccessAutoLogoffMinutes?: number;
  // Lot 8 Voir la mire de login (Option C)
  systemViewLoginScreenAllowed?: boolean;
  systemViewLoginScreenAutoStart?: boolean;
  // Chat
  allowUserOperatorChat?: boolean;
  chatAvailableBeforeConsent?: boolean;
  chatTranscriptAuditMode?: "metadata" | "full" | "disabled";
  // Trace debug
  traceAllowed?: boolean;
  traceBodiesAllowed?: boolean;
  // Audit
  auditPolicyDecisions?: boolean;
  // Enrollment (GPO/MDM)
  installToken?: string | null;
  installTokenAutoConsume?: boolean;
  // Provenance (debug : d'où vient chaque champ)
  source?: "gpo" | "local" | "config.json" | "defaults";
  readAt?: string;

  // Compat legacy (anciens payloads UI) — laissé en lecture seule.
  privacyScreenAllowed?: boolean;
  privacyScreenDefault?: boolean;
  localInputBlockAllowed?: boolean;
  chatAllowed?: boolean;
  fileTransferAllowed?: boolean;
}

/** Lot 4-bis politiques-host : session RDS/TS énumérée par WTSEnumerateSessionsEx côté host. */
export interface RdsSession {
  sessionId: number;
  sessionName: string;
  userName?: string | null;
  domainName?: string | null;
  state: string;
  isConsole: boolean;
  /** Lot E : session verrouillée (écran de verrouillage Windows). */
  locked?: boolean;
}

export interface SessionState {
  type: "session:state";
  sessionCode: string;
  supportApproved: boolean;
  supportRequest: { state: string; operatorLabel?: string };
  controlGranted: boolean;
  nativeAgentOnline: boolean;
  elevation: { state: string };
  activeOperatorPeerId: string | null;
  agentFeatures: AgentFeatures | null;
  agentDependencies?: AgentDependencyReport | null;
  peers: PeerInfo[];
  /** Lot 4 politiques-host : état d'access granulaire publié par le relai. */
  access?: AccessSnapshot | null;
  /** Lot 4 politiques-host : policy effective résolue côté host. */
  hostPolicy?: HostPolicy | null;
  /** Lot 4-bis politiques-host : sessions RDS énumérées par le host. */
  rdsSessions?: RdsSession[] | null;
  /** Lot 8 politiques-host : true si aucun user n'est connecté sur le poste (mire de login). */
  noUserLoggedIn?: boolean;
  /** Résolution adaptative : viewport négocié (MAX plafonné au CAP) publié par le relais — informatif HUD. */
  negotiatedViewport?: { w: number; h: number; dpr: number } | null;
  /** Résolution adaptative : sourceRect courant (mode 1:1/pan) — recale le mapping d'un opérateur qui rejoint en cours. */
  sourceRect?: { x: number; y: number; w: number; h: number } | null;
  /** Résolution adaptative : type de session ciblée par la capture. */
  sessionKind?: "console" | "rdp" | null;
}

export interface ToolRequest<P = unknown> {
  v: 1;
  family: "control";
  type: "tool";
  op: "request";
  id: string;
  payload: {
    requestId: string;
    tool: string;
    params: P;
  };
  deliveryClass?: "must_deliver";
  dropPolicy?: "never";
}

export interface ToolResultPayload<R = unknown> {
  requestId: string;
  tool: string;
  ok: boolean;
  result?: R;
  error?: string;
}

export interface ToolResult<R = unknown> {
  v: 1;
  family: "control";
  type: "tool";
  op: "result";
  id: string;
  payload: ToolResultPayload<R>;
}

export interface PluginJobEventPayload {
  type: "log" | "progress" | "done" | "error";
  line?: string;
  pct?: number;
  msg?: string;
  result?: unknown;
  error?: string;
}

export interface PluginJobEvent {
  v: 1;
  family: "live";
  type: "plugin";
  op: "job.event";
  id: string;
  payload: {
    pluginId: string;
    jobId: string;
    seq: number;
    event: PluginJobEventPayload;
  };
}

export type WsIncoming =
  | SessionState
  | { type: "session:features"; features: AgentFeatures }
  | AgentCredentialPublicKey
  | { type: "session:dependencies"; report: AgentDependencyReport }
  | ToolResult
  | { type: "session:joined" | "session:created"; sessionCode: string; peerId: string }
  | { type: "support:decision"; approved: boolean }
  | { type: "chat"; text: string; fromPeerId?: string }
  | { type: "frame"; data?: string; width?: number; height?: number; kind?: string }
  | { type: "operator:active"; label?: string }
  | { type: "audit:event"; event: { ts: string; action: string; detail?: string } }
  | { type: "clipboard:sync"; text?: string; kind?: "text" | "file"; name?: string; size?: number }
  | { type: "error"; code: string; message: string }
  | { type: "mcp:confirm_request"; requestId: string; tool: string; sessionCode?: string }
  // WS-O (Vague 3) — réponse de l'agent au handshake E2E (sa clé publique ECDH
  // SPKI base64 + sel HKDF base64).
  | { type: "e2e:key"; suite: string; pub: string; salt: string }
  // WS-O — l'agent signale une inspection TLS (MITM/proxy firewall) détectée au
  // handshake WSS. Purement informatif (jamais bloquant).
  | {
      type: "e2e:inspection-detected";
      fingerprint: string;
      issuer: string;
      subject: string;
      reason: string;
      e2eAvailable?: boolean;
    }
  | PluginJobEvent
  | { type: string; [key: string]: unknown };

export type WsOutgoing =
  | { type: "session:join"; role: "operator"; sessionCode: string; operatorToken?: string }
  | { type: "session:create"; role: "operator" }
  | { type: "operator:capabilities"; capabilities: Record<string, unknown> }
  | { type: "support:decision"; approved: boolean }
  | { type: "control:grant"; granted: boolean }
  | { type: "input"; input: unknown }
  | { type: "elevation:request" }
  | { type: "sas:request" }
  | { type: "clipboard:request" }
  | { type: "clipboard:set"; text: string }
  | ToolRequest
  | { type: "chat"; text: string }
  // WS-O (Vague 3) — handshake E2E : l'opérateur envoie sa clé publique ECDH
  // éphémère (SPKI base64) + l'identifiant de suite.
  | { type: "e2e:hello"; suite: string; pub: string }
  | { type: string; [key: string]: unknown };
